import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X, PackageSearch } from "lucide-react";
import { api, getPosition } from "@/lib/api";
import { PartCard, Loader, Empty } from "@/components/shared";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { LocationBar } from "@/components/LocationBar";
import { useLocationCtx } from "@/context/LocationContext";
import { VehiclePicker } from "@/components/VehiclePicker";

const SORTS = [
  { v: "newest", l: "الأحدث" }, { v: "cheapest", l: "الأرخص" },
  { v: "expensive", l: "الأغلى" }, { v: "rating", l: "الأعلى تقييمًا" },
  { v: "nearest", l: "الأقرب" },
];

export default function SearchPage() {
  const [sp, setSp] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [cities, setCities] = useState([]);
  const [cats, setCats] = useState([]);
  const [makes, setMakes] = useState([]);
  const { coords, requestLocation } = useLocationCtx();

  const get = (k) => sp.get(k) || "";
  const setParam = (k, v) => {
    const n = new URLSearchParams(sp);
    if (v) n.set(k, v); else n.delete(k);
    setSp(n);
  };

  const onSortChange = async (v) => {
    if (v === "nearest" && !coords) {
      toast.info("جارٍ تحديد موقعك...");
      const c = await requestLocation();
      if (!c) { toast.error("تعذّر تحديد موقعك. فعّل صلاحية الموقع أو اختر المدينة."); return; }
    }
    setParam("sort", v);
  };

  useEffect(() => {
    Promise.all([api.get("/cities"), api.get("/categories"), api.get("/vehicles/makes")])
      .then(([c, cat, m]) => { setCities(c.data); setCats(cat.data); setMakes(m.data); });
  }, []);

  const fetchParts = useCallback(() => {
    setLoading(true);
    const params = {};
    ["q", "make", "model", "year", "category_id", "city", "min_price", "max_price", "condition"].forEach((k) => {
      if (sp.get(k)) params[k] = sp.get(k);
    });
    params.sort = sp.get("sort") || "newest";
    if (coords) { params.lat = coords.lat; params.lng = coords.lng; }
    api.get("/parts", { params }).then(({ data }) => { setItems(data.items); setTotal(data.total); })
      .finally(() => setLoading(false));
  }, [sp, coords]);

  useEffect(() => { fetchParts(); }, [fetchParts]);

  const Filters = () => (
    <div className="space-y-5">
      <div>
        <label className="text-xs font-bold text-[var(--text-muted)] mb-2 block">القسم</label>
        <select data-testid="filter-category" value={get("category_id")} onChange={(e) => setParam("category_id", e.target.value)}
          className="w-full rounded-xl bg-[var(--bg-secondary)] px-3 py-2.5 text-white outline-none">
          <option value="">كل الأقسام</option>
          {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      <div>
        <label className="text-xs font-bold text-[var(--text-muted)] mb-2 block">السيارة</label>
        <VehiclePicker includeTrim={false}
          value={{ make: get("make"), model: get("model"), year: get("year") }}
          onChange={(v) => {
            const n = new URLSearchParams(sp);
            v.make ? n.set("make", v.make) : n.delete("make");
            v.model ? n.set("model", v.model) : n.delete("model");
            v.year ? n.set("year", String(v.year)) : n.delete("year");
            setSp(n);
          }} />
      </div>
      <div>
        <label className="text-xs font-bold text-[var(--text-muted)] mb-2 block">المدينة</label>
        <select data-testid="filter-city" value={get("city")} onChange={(e) => setParam("city", e.target.value)}
          className="w-full rounded-xl bg-[var(--bg-secondary)] px-3 py-2.5 text-white outline-none">
          <option value="">كل المدن</option>
          {cities.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
        </select>
      </div>
      <div>
        <label className="text-xs font-bold text-[var(--text-muted)] mb-2 block">نطاق السعر ({"ر.س"})</label>
        <div className="flex gap-2">
          <input type="number" data-testid="filter-min-price" placeholder="من" value={get("min_price")} onChange={(e) => setParam("min_price", e.target.value)}
            className="w-full rounded-xl bg-[var(--bg-secondary)] px-3 py-2.5 text-white outline-none" />
          <input type="number" data-testid="filter-max-price" placeholder="إلى" value={get("max_price")} onChange={(e) => setParam("max_price", e.target.value)}
            className="w-full rounded-xl bg-[var(--bg-secondary)] px-3 py-2.5 text-white outline-none" />
        </div>
      </div>
      <button data-testid="clear-filters" onClick={() => setSp(new URLSearchParams())}
        className="w-full rounded-xl border border-[var(--border-c)] py-2.5 text-sm text-[var(--text-muted)] hover:text-white">
        مسح الفلاتر
      </button>
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
      <div className="mb-4"><LocationBar /></div>
      {/* search bar */}
      <div className="ts-card p-4 mb-6 flex flex-col sm:flex-row gap-3">
        <div className="flex-1 flex items-center gap-2 rounded-xl bg-[var(--bg-secondary)] px-4">
          <PackageSearch size={20} className="text-[var(--text-muted)]" />
          <input data-testid="search-input" defaultValue={get("q")} placeholder="ابحث بالاسم أو رقم القطعة"
            onKeyDown={(e) => e.key === "Enter" && setParam("q", e.target.value)}
            className="w-full bg-transparent py-3 text-white outline-none" />
        </div>
        <select data-testid="sort-select" value={get("sort") || "newest"} onChange={(e) => onSortChange(e.target.value)}
          className="rounded-xl bg-[var(--bg-secondary)] px-4 py-3 text-white outline-none">
          {SORTS.map((s) => <option key={s.v} value={s.v}>ترتيب: {s.l}</option>)}
        </select>
        <Sheet>
          <SheetTrigger asChild>
            <button data-testid="mobile-filter-button" className="lg:hidden btn-amber justify-center"><SlidersHorizontal size={18} /> فلترة</button>
          </SheetTrigger>
          <SheetContent side="right" className="bg-[var(--bg-card)] border-[var(--border-c)] text-white w-80">
            <SheetHeader><SheetTitle className="text-white text-right">الفلاتر</SheetTitle></SheetHeader>
            <div className="mt-6"><Filters /></div>
          </SheetContent>
        </Sheet>
      </div>

      <div className="flex gap-6">
        <aside className="hidden lg:block w-64 shrink-0">
          <div className="ts-card p-5 sticky top-24"><Filters /></div>
        </aside>
        <div className="flex-1">
          <p className="text-sm text-[var(--text-muted)] mb-4">{total} نتيجة</p>
          {loading ? <Loader /> : items.length === 0 ? (
            <div className="ts-card p-8 text-center" data-testid="search-no-results">
              <PackageSearch size={40} className="mx-auto text-[var(--amber)] mb-3" />
              <p className="font-head text-lg font-black text-white mb-1">ما لقيت القطعة؟</p>
              <p className="text-sm text-[var(--text-muted)] mb-5">أرسل طلبًا للتشاليح واستقبل عروضًا بأسعار مختلفة لتقارن وتختار الأنسب.</p>
              <a data-testid="search-request-part-btn"
                href={`/request-part?make=${encodeURIComponent(get("make"))}&q=${encodeURIComponent(get("q"))}`}
                className="btn-amber inline-flex">اطلب قطعة الآن</a>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
              {items.map((p) => <PartCard key={p.id} part={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
